import { CurrencyIcon, DropdownChevron } from '@components'
import cn from '@integrations/classnames'
import { useEffect, useState } from '@integrations/react'
import type { Currency } from 'mmfintech-backend-api/types'
import { isValidString, tr, useDropDownMenu } from 'mmfintech-commons'
import surfaceStyles from '../SelectorSurface/SelectorSurface.module.scss'
import styles from './SelectCurrency.module.scss'

interface Props {
  staticDisplay?: boolean
  currencies?: Currency[]
  error?: string
  label?: string
  required?: boolean
  selected?: string
  setSelected?: (value: string) => void
}

function SelectCurrency({ staticDisplay = false, currencies, error, label, required, selected, setSelected }: Props) {
  const { visible, hideDropDown, toggleDropDown, setRef } = useDropDownMenu()

  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(null)

  function toggle() {
    !staticDisplay && toggleDropDown()
  }

  function handleSelect(currency?: Currency): void {
    hideDropDown()
    setSelected(currency.currencyCode)
  }

  function prepareCurrencyName(currency: Currency): string {
    return currency
      ? currency.currencyCode === currency.currencyFullName
        ? currency.currencyCode
        : `${currency.currencyCode} - ${currency.currencyFullName}`
      : selected
  }

  useEffect(() => {
    setSelectedCurrency(currencies.find((c) => c.currencyCode === selected))
  }, [selected, currencies.find])

  return (
    <div
      className={cn(surfaceStyles['selector-wrapper'], styles['select-currency-wrapper'], 'select-currency-wrapper')}
    >
      {label && (
        <div className={cn(styles['select-currency-label'], 'select-currency-label')}>
          {label}
          {required ? <span className={cn(styles['required'], 'required')}>*</span> : null}
        </div>
      )}
      <div className={cn(surfaceStyles['selector-root'], 'select-currency-styled')}>
        <div
          className={cn(surfaceStyles['selector-control'], styles['currency-button-styled'], 'currency-button-styled', {
            [surfaceStyles['static']]: staticDisplay,
            static: staticDisplay,
            [surfaceStyles['error']]: error?.length,
            error: error?.length
          })}
          ref={visible ? setRef : null}
          onClick={toggle}
        >
          {selected ? (
            <span className={cn(surfaceStyles['selector-selection'], 'currency-info')}>
              <span className={cn(surfaceStyles['selector-selection-icon'], 'icon')}>
                <CurrencyIcon currency={selected} size='26px' />
              </span>
              <span className={cn(surfaceStyles['selector-selection-content'], styles['content'], 'content')}>
                {prepareCurrencyName(selectedCurrency)}
              </span>
            </span>
          ) : (
            <span className={cn(surfaceStyles['selector-placeholder'], styles['no-currency'], 'no-currency')}>
              {tr('FRONTEND.COMPONENTS.SELECT_CURRENCY.PLACEHOLDER', 'Select currency')}
            </span>
          )}
          {!staticDisplay && <DropdownChevron className={cn('chevron')} expanded={visible} />}
        </div>

        {!staticDisplay && (
          <div
            className={cn(surfaceStyles['selector-menu'], 'currency-list-styled', {
              [surfaceStyles['is-opened']]: visible,
              'is-opened': visible
            })}
            data-test='currencies-menu-content'
          >
            {Array.isArray(currencies) &&
              currencies.map((currency) => {
                return (
                  <div
                    className={cn(
                      surfaceStyles['selector-option'],
                      styles['currency-styled'],
                      'currency-styled',
                      'dropdown-option'
                    )}
                    key={currency.currencyCode}
                    onClick={() => handleSelect(currency)}
                    data-test='currency-menu-item'
                  >
                    <span className={cn(surfaceStyles['selector-option-icon'], 'icon')}>
                      <CurrencyIcon currency={currency.currencyCode} size='26px' />
                    </span>
                    <span className={cn(surfaceStyles['selector-option-content'], styles['content'], 'content')}>
                      {prepareCurrencyName(currency)}
                    </span>
                  </div>
                )
              })}
          </div>
        )}
      </div>

      {isValidString(error) && (
        <div className={cn(surfaceStyles['selector-error'], 'select-currency-error')}>{error}</div>
      )}
    </div>
  )
}

export default SelectCurrency
